import type {ReactNode} from 'react'
import type {SgsstItem} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {cn} from '@/lib/utils'

export type GroupEstado = 'no_cumple' | 'sin_calificar' | 'resuelto'

const TONE: Record<GroupEstado, string> = {
  no_cumple: 'text-tone-red-fg',
  sin_calificar: 'text-tone-amber-fg',
  resuelto: 'text-primary',
}
const RAIL: Record<GroupEstado, string> = {
  no_cumple: 'border-l-tone-red-fg',
  sin_calificar: 'border-l-tone-amber-fg',
  resuelto: 'border-l-primary',
}
const BAR: Record<GroupEstado, string> = {
  no_cumple: 'bg-tone-red-fg',
  sin_calificar: 'bg-tone-amber-fg',
  resuelto: 'bg-primary',
}

export function resumenDelGrupo(items: SgsstItem[]): {earned: number; possible: number; estado: GroupEstado} {
  const earned = items
    .filter((item) => item.status === 'cumple' || item.status === 'no_aplica')
    .reduce((sum, item) => sum + item.weight, 0)
  const possible = items.reduce((sum, item) => sum + item.weight, 0)
  const estado: GroupEstado = items.some((item) => item.status === 'no_cumple')
    ? 'no_cumple'
    : items.some((item) => !item.status)
      ? 'sin_calificar'
      : 'resuelto'
  return {earned, possible, estado}
}

export function ChecklistGroupCard({
  groupName,
  earned,
  possible,
  estado,
  ayuda,
  children,
}: {
  groupName: string
  earned: number
  possible: number
  estado: GroupEstado
  /** U4: una ayuda por grupo, nunca por fila. */
  ayuda?: ReactNode
  children: ReactNode
}) {
  return (
    <details open className={cn('surface overflow-hidden border-l-[3px]', RAIL[estado])} data-testid="checklist-group">
      <summary className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3 hover:bg-secondary/40">
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden="true"
            className={cn('inline-block size-1.5 shrink-0 rounded-full bg-current', TONE[estado])}
            data-testid="checklist-group-dot"
          />
          <span className="text-sm font-medium text-foreground">{groupName}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <span aria-hidden="true" className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-track sm:block">
            <span
              className={cn('block h-full rounded-full', BAR[estado])}
              style={{width: `${possible ? (earned / possible) * 100 : 0}%`}}
            />
          </span>
          <span className={cn('font-mono text-xs', TONE[estado])} data-testid="checklist-group-total">
            {earned.toLocaleString('es-CO')} / {possible.toLocaleString('es-CO')}
          </span>
        </span>
      </summary>
      {/* Fuera del `<summary>`: adentro entra en el nombre accesible del plegable (U15/U17) y su
          clic alterna el `<details>`. */}
      {ayuda && <div className="flex justify-end px-4 pt-2">{ayuda}</div>}
      <ul className="surface-divider border-t px-4">{children}</ul>
    </details>
  )
}
